import React, { useState, useEffect, useRef } from "react";
import SmartImage from "./SmartImage";

import "./GalleryGrid.css";

export function GalleryGrid({
  images,
  maxHeight = "400px",
  minHeight = "300px",
  animationSpeed = 3000,
  highQuality = false,
}) {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1000);
  const [currentImages, setCurrentImages] = useState([0, 1]); // Desktop: [left, right], Mobile: [single]
  const [nextSlot, setNextSlot] = useState(0); // Which slot to replace next (0 = left/single, 1 = right)
  const [isTransitioning, setIsTransitioning] = useState([false, false]);
  const [isInView, setIsInView] = useState(false);
  const galleryRef = useRef(null);

  // Intersection Observer to detect when gallery is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.3, // Start when 30% of the gallery is visible
        rootMargin: "50px", // Start 50px before it comes into view
      }
    );

    if (galleryRef.current) {
      observer.observe(galleryRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth > 1000;
      setIsDesktop(desktop);
      if (!desktop) {
        // On mobile, only show one image
        setCurrentImages([currentImages[0]]);
        setNextSlot(0);
      } else {
        // On desktop, ensure we have two images
        if (currentImages.length === 1) {
          setCurrentImages([
            currentImages[0],
            (currentImages[0] + 1) % images.length,
          ]);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentImages, images.length]);

  // Auto-cycle images every 5 seconds when in view
  useEffect(() => {
    if (!isInView || !images || images.length === 0) return; // Only run when gallery is visible and has images

    const interval = setInterval(() => {
      if (isDesktop) {
        // Desktop: Replace one image at a time, alternating between left and right
        const slotToReplace = nextSlot;
        const currentImageIndex = currentImages[slotToReplace];

        // Find next available image that's not currently shown
        let nextImageIndex = (currentImageIndex + 1) % images.length;
        while (currentImages.includes(nextImageIndex) && images.length > 2) {
          nextImageIndex = (nextImageIndex + 1) % images.length;
        }

        // Start transition
        setIsTransitioning((prev) => {
          const newTransitioning = [...prev];
          newTransitioning[slotToReplace] = true;
          return newTransitioning;
        });

        // After fade out, change image and fade in
        setTimeout(() => {
          setCurrentImages((prev) => {
            const newImages = [...prev];
            newImages[slotToReplace] = nextImageIndex;
            return newImages;
          });

          setTimeout(() => {
            setIsTransitioning((prev) => {
              const newTransitioning = [...prev];
              newTransitioning[slotToReplace] = false;
              return newTransitioning;
            });
          }, 50);
        }, 300);

        // Alternate between left (0) and right (1) slots
        setNextSlot((prev) => (prev === 0 ? 1 : 0));
      } else {
        // Mobile: Replace the single image
        const currentImageIndex = currentImages[0];
        const nextImageIndex = (currentImageIndex + 1) % images.length;

        setIsTransitioning([true]);
        setTimeout(() => {
          setCurrentImages([nextImageIndex]);
          setTimeout(() => {
            setIsTransitioning([false]);
          }, 50);
        }, 300);
      }
    }, animationSpeed);

    return () => clearInterval(interval);
  }, [currentImages, nextSlot, isDesktop, images, isInView, animationSpeed]);

  // Early return if no images provided
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div
      ref={galleryRef}
      className={`auto-gallery ${isDesktop ? "desktop" : "mobile"}`}
      style={{ position: "relative", marginBottom: -8 }}
    >
      <div className="gallery-container">
        {isDesktop ? (
          // Desktop: Two images side by side
          <div className="gallery-row">
            <div
              className={`gallery-slot ${isTransitioning[0] ? "fading" : ""}`}
            >
              <SmartImage
                src={images[currentImages[0]].src}
                alt={images[currentImages[0]].alt}
                className="gallery-image"
                width={1200}
                crop="fill"
                gravity="center"
                style={{
                  maxHeight: maxHeight,
                  minHeight: minHeight,
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                }}
                highQuality={highQuality}
              />
            </div>
            <div
              className={`gallery-slot ${isTransitioning[1] ? "fading" : ""}`}
            >
              <SmartImage
                src={images[currentImages[1]].src}
                alt={images[currentImages[1]].alt}
                className="gallery-image"
                width={1200}
                crop="fill"
                gravity="center"
                style={{
                  maxHeight: maxHeight,
                  minHeight: minHeight,
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                }}
                highQuality={highQuality}
              />
            </div>
          </div>
        ) : (
          // Mobile: Single image
          <div className="gallery-row">
            <div
              className={`gallery-slot ${isTransitioning[0] ? "fading" : ""}`}
            >
              <SmartImage
                src={images[currentImages[0]].src}
                alt={images[currentImages[0]].alt}
                className="gallery-image"
                width={1200}
                crop="fill"
                gravity="center"
                style={{
                  maxHeight: maxHeight,
                  minHeight: minHeight,
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                }}
                highQuality={highQuality}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
